<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue';
import { SignatureFieldType } from '@/api';
import GaiaPlacedField from './GaiaPlacedField.vue';
import { signatureFieldTypeMap } from '../statusMaps';
import { gaiaNeutral, gaiaSignerPalette } from '../themes/tokens';
import type {
  SignatureDraftField,
  SignatureFieldPaletteItem,
  SignatureReviewParty,
} from '@/types/contractManagement/signatureFlow';
import { isEnumValue } from '@/utils/enumUtils';
import { initialsOf } from '@/utils/initials';
import { DEFAULT_FIELD_FONT_SIZE_PT, fieldFit, fieldPreviewText } from '@/utils/signatureFieldText';

/**
 * The field-placement surface that sits on top of the rendered PDF page
 * (wizard step 4). It owns the geometry — drop, draw, move, resize — and
 * renders each field as a `GaiaPlacedField`.
 *
 * Coordinates are percentages of the rendered page, matching what the backend
 * stores (see `services/integration/signatureFieldService.ts`), so a field
 * keeps its place at any zoom or page size. They are kept to two decimals, not
 * whole percent, so a field lands where it was dropped. Minimum sizes are in px
 * because they are about what a person can still hit, not about the page —
 * except the signature's, which is the provider's minimum in pt.
 */

const DEFAULT_FIELD_WIDTH_PX = 200;
const DEFAULT_FIELD_HEIGHT_PX = 75;
const DEFAULT_CHECKBOX_SIDE_PX = 48;
const MIN_CHECKBOX_SIDE_PX = 37;
const MIN_FIELD_WIDTH_PX = 85;
const MIN_FIELD_HEIGHT_PX = 37;
/** The provider rejects a signature box smaller than this. */
const MIN_SIGNATURE_WIDTH_PT = 85;
const MIN_SIGNATURE_HEIGHT_PT = 37;
/** Drags smaller than this on both axes count as plain clicks. */
const DRAW_ACTIVATION_PX = 8;

/** A share of the page, kept to two decimals. */
function toPercent(value: number): number {
  return Math.round(value * 100) / 100;
}

const props = defineProps({
  /** Fields on the active document — the canvas filters to the current page. */
  fields: {
    type: Array as PropType<SignatureDraftField[]>,
    required: true,
  },
  parties: {
    type: Array as PropType<SignatureReviewParty[]>,
    required: true,
  },
  selectedFieldUid: {
    type: String as PropType<string | null>,
    default: null,
  },
  currentPage: {
    type: Number,
    required: true,
  },
  /** The page's width in PDF points, to size pt-based things on screen. */
  pageWidthPts: {
    type: Number as PropType<number | null>,
    default: null,
  },
  /** Sample answers the author typed to try a Text field, by field uid (preview only). */
  samples: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
  /** Armed palette entry — drawing a rectangle places a field of this type. */
  activeFieldType: {
    type: Object as PropType<SignatureFieldPaletteItem | null>,
    default: null,
  },
});

const emit = defineEmits<{
  'field-dropped': [
    payload: {
      type: SignatureFieldType;
      x: number;
      y: number;
      width: number;
      height: number;
      presetQuestion?: string;
    },
  ];
  'field-moved': [payload: { uid: string; x: number; y: number; page: number }];
  'field-resized': [payload: { uid: string; width: number; height: number }];
  'field-clicked': [uid: string];
  'field-removed': [uid: string];
  /** The page's size in PDF points, once measured — the settings panel sizes text with it. */
  'page-measured': [size: { widthPts: number; heightPts: number }];
}>();

const overlayRef = ref<HTMLDivElement | null>(null);
const overlaySize = ref({ width: 0, height: 0 });
let overlayResizeObserver: ResizeObserver | null = null;

interface FieldDimensions {
  width: number;
  height: number;
}

function updateOverlaySize(): void {
  if (!overlayRef.value) return;

  const rect = overlayRef.value.getBoundingClientRect();
  overlaySize.value = { width: rect.width, height: rect.height };
}

/** A checkbox stays square on screen, whatever the page's aspect ratio is. */
function renderedDimensions(field: SignatureDraftField): FieldDimensions {
  const width = field.width ?? 20;
  const height = field.height ?? 8;

  if (
    field.type !== SignatureFieldType.CHECKBOX ||
    overlaySize.value.width <= 0 ||
    overlaySize.value.height <= 0
  ) {
    return { width, height };
  }

  const widthPx = (width / 100) * overlaySize.value.width;
  const heightPx = (height / 100) * overlaySize.value.height;
  const sidePx = Math.max(1, Math.min(widthPx, heightPx));

  return {
    width: (sidePx / overlaySize.value.width) * 100,
    height: (sidePx / overlaySize.value.height) * 100,
  };
}

/** Rendered px per PDF point; 0 until both the page and the overlay are measured. */
const pxPerPt = computed(() =>
  props.pageWidthPts && overlaySize.value.width > 0
    ? overlaySize.value.width / props.pageWidthPts
    : 0
);

/** The smallest box a field may be drawn or resized to, in px. */
function minSizePx(type: SignatureFieldType): FieldDimensions {
  if (type === SignatureFieldType.CHECKBOX) {
    return { width: MIN_CHECKBOX_SIDE_PX, height: MIN_CHECKBOX_SIDE_PX };
  }
  if (type === SignatureFieldType.SIGNATURE) {
    return {
      width: Math.max(MIN_FIELD_WIDTH_PX, MIN_SIGNATURE_WIDTH_PT * pxPerPt.value),
      height: Math.max(MIN_FIELD_HEIGHT_PX, MIN_SIGNATURE_HEIGHT_PT * pxPerPt.value),
    };
  }
  return { width: MIN_FIELD_WIDTH_PX, height: MIN_FIELD_HEIGHT_PX };
}

const currentPageFields = computed(() =>
  props.fields.filter(field => field.page === props.currentPage)
);

// ── Signer identity ─────────────────────────────────────────────────────────

/** Positional colour, so a signer keeps the same colour across the wizard. */
function partyColor(recipientId: string | null | undefined): string {
  if (!recipientId) return gaiaNeutral.textFaint;

  const index = props.parties.findIndex(party => party.uid === recipientId);
  return index === -1 ? gaiaNeutral.textFaint : gaiaSignerPalette[index % gaiaSignerPalette.length];
}

function partyName(recipientId: string | null | undefined): string {
  const party = props.parties.find(entry => entry.uid === recipientId);
  if (!party) return '';

  return party.name;
}

/** A Text field reads as its question ("City", "Capacity"), the rest as their type. */
function fieldLabel(field: SignatureDraftField): string {
  const question = field.type === SignatureFieldType.TEXT ? field.question?.trim() : undefined;
  if (question) return question;
  return signatureFieldTypeMap[field.type];
}

/** The page's height in points: the rendered height at the same px-per-pt as its width. */
const pageHeightPts = computed(() =>
  pxPerPt.value > 0 ? overlaySize.value.height / pxPerPt.value : 0
);

watch(pageHeightPts, heightPts => {
  if (props.pageWidthPts && heightPts > 0) {
    emit('page-measured', { widthPts: props.pageWidthPts, heightPts });
  }
});

/**
 * What the box shows, at the size the PDF will set it: the fixed text, the
 * signer's real name or email, today's date, or a sample answer — and whether
 * it fits the box. Null for fields without text (signature, checkbox).
 */
function fieldPreview(field: SignatureDraftField): {
  text: string;
  fixed: boolean;
  fits: boolean;
  /** Whole lines the box holds — the provider prints no partial line. Null until measured. */
  lines: number | null;
} | null {
  const party = props.parties.find(entry => entry.uid === field.recipient_id);
  const preview = fieldPreviewText(field, party, props.samples[field.uid]);
  if (!preview) return null;
  if (!props.pageWidthPts || pageHeightPts.value <= 0) {
    return { ...preview, fits: true, lines: null };
  }
  const fit = fieldFit(
    preview.text,
    field.font_size ?? DEFAULT_FIELD_FONT_SIZE_PT,
    ((field.width ?? 20) / 100) * props.pageWidthPts,
    ((field.height ?? 8) / 100) * pageHeightPts.value
  );
  return { ...preview, fits: fit.fits, lines: fit.capacity.lines };
}

/** The field's font size on screen, so the box shows what the PDF will. */
function fieldFontSize(field: SignatureDraftField): string | undefined {
  if (!pxPerPt.value) return undefined;
  return `${(field.font_size ?? DEFAULT_FIELD_FONT_SIZE_PT) * pxPerPt.value}px`;
}

// ── Drop to place (drag from the palette) ───────────────────────────────────

function onDragOver(event: DragEvent): void {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
}

function onDrop(event: DragEvent): void {
  event.preventDefault();

  const fieldType = event.dataTransfer?.getData('fieldType');
  if (!isEnumValue(SignatureFieldType, fieldType) || !overlayRef.value) return;

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
  const presetQuestion = event.dataTransfer?.getData('presetQuestion') || undefined;
  const rect = overlayRef.value.getBoundingClientRect();
  const x = toPercent(((event.clientX - rect.left) / rect.width) * 100);
  const y = toPercent(((event.clientY - rect.top) / rect.height) * 100);

  const isCheckbox = fieldType === SignatureFieldType.CHECKBOX;
  const minimum = minSizePx(fieldType);
  const widthPx = Math.max(
    minimum.width,
    isCheckbox ? DEFAULT_CHECKBOX_SIDE_PX : DEFAULT_FIELD_WIDTH_PX
  );
  const heightPx = Math.max(
    minimum.height,
    isCheckbox ? DEFAULT_CHECKBOX_SIDE_PX : DEFAULT_FIELD_HEIGHT_PX
  );
  const width = toPercent((widthPx / rect.width) * 100);
  const height = toPercent((heightPx / rect.height) * 100);

  emit('field-dropped', { type: fieldType, x, y, width, height, presetQuestion });
}

// ── Draw to place (armed palette entry) ─────────────────────────────────────

interface DrawState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

const drawing = ref<DrawState | null>(null);

function drawRectPx(state: DrawState): {
  left: number;
  top: number;
  width: number;
  height: number;
} {
  return {
    left: Math.min(state.startX, state.currentX),
    top: Math.min(state.startY, state.currentY),
    width: Math.abs(state.currentX - state.startX),
    height: Math.abs(state.currentY - state.startY),
  };
}

const drawPreview = computed(() => {
  if (!drawing.value) return null;

  const rect = drawRectPx(drawing.value);
  return {
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  };
});

/** Safe to call repeatedly — no path may leave window listeners attached. */
function stopDrawInteraction(): void {
  drawing.value = null;
  window.removeEventListener('mousemove', onDrawMouseMove);
  window.removeEventListener('mouseup', onDrawMouseUp);
}

function onOverlayMouseDown(event: MouseEvent): void {
  if (!props.activeFieldType || !overlayRef.value || event.button !== 0) return;

  event.preventDefault();
  const rect = overlayRef.value.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;

  drawing.value = { startX: x, startY: y, currentX: x, currentY: y };
  window.addEventListener('mousemove', onDrawMouseMove);
  window.addEventListener('mouseup', onDrawMouseUp);
}

function onDrawMouseMove(event: MouseEvent): void {
  if (!drawing.value || !overlayRef.value) return;

  const rect = overlayRef.value.getBoundingClientRect();
  drawing.value.currentX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
  drawing.value.currentY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
}

function onDrawMouseUp(): void {
  const state = drawing.value;
  const fieldType = props.activeFieldType;
  // Tear down first: no early return may leave the listeners behind.
  stopDrawInteraction();

  if (!state || !fieldType || !overlayRef.value) return;

  const overlayRect = overlayRef.value.getBoundingClientRect();
  const drawn = drawRectPx(state);

  if (drawn.width < DRAW_ACTIVATION_PX && drawn.height < DRAW_ACTIVATION_PX) return;

  const minimum = minSizePx(fieldType.type);
  const widthPx = Math.max(minimum.width, drawn.width);
  const heightPx = Math.max(minimum.height, drawn.height);
  const width = toPercent((widthPx / overlayRect.width) * 100);
  const height = toPercent((heightPx / overlayRect.height) * 100);

  emit('field-dropped', {
    type: fieldType.type,
    x: toPercent(Math.max(0, Math.min(100 - width, (drawn.left / overlayRect.width) * 100))),
    y: toPercent(Math.max(0, Math.min(100 - height, (drawn.top / overlayRect.height) * 100))),
    width,
    height,
    presetQuestion: fieldType.presetQuestion,
  });
}

// ── Move ────────────────────────────────────────────────────────────────────
//
// A field can be dragged onto another page. Every page carries its own canvas,
// so the target is whichever canvas is under the cursor, found by its
// `data-signature-page`. The position is measured against that canvas's
// current rect, not as a delta from where the drag began, so it also stays
// right when the pane scrolls mid-drag.

interface MoveState {
  uid: string;
  /** Where the cursor grabbed the field, in px from its top-left corner. */
  grabOffsetX: number;
  grabOffsetY: number;
  fieldWidth: number;
  fieldHeight: number;
  /** The last position over a page — kept while the cursor is between pages. */
  last: { x: number; y: number; page: number };
}

const moving = ref<MoveState | null>(null);

function stopMoveInteraction(): void {
  moving.value = null;
  window.removeEventListener('mousemove', onMoveMouseMove);
  window.removeEventListener('mouseup', onMoveMouseUp);
}

function onFieldMouseDown(event: MouseEvent, field: SignatureDraftField): void {
  event.stopPropagation();
  const dimensions = renderedDimensions(field);
  const fieldRect = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect();

  moving.value = {
    uid: field.uid,
    grabOffsetX: fieldRect ? event.clientX - fieldRect.left : 0,
    grabOffsetY: fieldRect ? event.clientY - fieldRect.top : 0,
    fieldWidth: dimensions.width,
    fieldHeight: dimensions.height,
    last: { x: field.x, y: field.y, page: field.page },
  };
  window.addEventListener('mousemove', onMoveMouseMove);
  window.addEventListener('mouseup', onMoveMouseUp);
}

/** The page canvas under the cursor, if any. */
function canvasAt(clientX: number, clientY: number): HTMLElement | null {
  for (const element of document.elementsFromPoint(clientX, clientY)) {
    const canvas = element.closest<HTMLElement>('[data-signature-page]');
    if (canvas) return canvas;
  }
  return null;
}

function movedPosition(
  event: MouseEvent,
  state: MoveState
): { x: number; y: number; page: number } {
  const canvas = canvasAt(event.clientX, event.clientY);
  const page = Number(canvas?.dataset.signaturePage);
  if (!canvas || !page) return state.last;

  const rect = canvas.getBoundingClientRect();
  const x = ((event.clientX - state.grabOffsetX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - state.grabOffsetY - rect.top) / rect.height) * 100;

  state.last = {
    x: toPercent(Math.max(0, Math.min(100 - state.fieldWidth, x))),
    y: toPercent(Math.max(0, Math.min(100 - state.fieldHeight, y))),
    page,
  };
  return state.last;
}

/**
 * The drag writes straight onto the field so the box tracks the cursor without
 * a round trip through the wizard's state; mouseup emits the committed value.
 * Writing `page` hands the box to the target page's canvas while this one keeps
 * the listeners.
 */
function onMoveMouseMove(event: MouseEvent): void {
  if (!moving.value) return;

  const position = movedPosition(event, moving.value);

  const field = props.fields.find(entry => entry.uid === moving.value?.uid);
  if (field) {
    field.x = position.x;
    field.y = position.y;
    field.page = position.page;
  }
}

function onMoveMouseUp(event: MouseEvent): void {
  const state = moving.value;
  stopMoveInteraction();

  if (!state) return;

  emit('field-moved', { uid: state.uid, ...movedPosition(event, state) });
}

// ── Resize ──────────────────────────────────────────────────────────────────

interface ResizeState {
  uid: string;
  fieldType: SignatureFieldType;
  startMouseX: number;
  startMouseY: number;
  startW: number;
  startH: number;
  fieldX: number;
  fieldY: number;
}

const resizing = ref<ResizeState | null>(null);

function stopResizeInteraction(): void {
  resizing.value = null;
  window.removeEventListener('mousemove', onResizeMouseMove);
  window.removeEventListener('mouseup', onResizeMouseUp);
}

function onResizeStart(event: MouseEvent, field: SignatureDraftField): void {
  event.stopPropagation();
  event.preventDefault();
  const dimensions = renderedDimensions(field);

  resizing.value = {
    uid: field.uid,
    fieldType: field.type,
    startMouseX: event.clientX,
    startMouseY: event.clientY,
    startW: dimensions.width,
    startH: dimensions.height,
    fieldX: field.x,
    fieldY: field.y,
  };
  window.addEventListener('mousemove', onResizeMouseMove);
  window.addEventListener('mouseup', onResizeMouseUp);
}

function resizedDimensions(event: MouseEvent, state: ResizeState): FieldDimensions | null {
  if (!overlayRef.value) return null;

  const rect = overlayRef.value.getBoundingClientRect();
  const maxW = 100 - state.fieldX;
  const maxH = 100 - state.fieldY;

  // A checkbox resizes as a square, driven by whichever axis moved further.
  if (state.fieldType === SignatureFieldType.CHECKBOX) {
    const startSidePx = Math.min(
      (state.startW / 100) * rect.width,
      (state.startH / 100) * rect.height
    );
    const deltaX = event.clientX - state.startMouseX;
    const deltaY = event.clientY - state.startMouseY;
    const sideDeltaPx = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
    const maxSidePx = Math.min((maxW / 100) * rect.width, (maxH / 100) * rect.height);
    const sidePx = Math.round(
      Math.max(MIN_CHECKBOX_SIDE_PX, Math.min(maxSidePx, startSidePx + sideDeltaPx))
    );

    return {
      width: toPercent((sidePx / rect.width) * 100),
      height: toPercent((sidePx / rect.height) * 100),
    };
  }

  const dw = ((event.clientX - state.startMouseX) / rect.width) * 100;
  const dh = ((event.clientY - state.startMouseY) / rect.height) * 100;
  const minimum = minSizePx(state.fieldType);
  const minW = (minimum.width / rect.width) * 100;
  const minH = (minimum.height / rect.height) * 100;

  return {
    width: toPercent(Math.max(minW, Math.min(maxW, state.startW + dw))),
    height: toPercent(Math.max(minH, Math.min(maxH, state.startH + dh))),
  };
}

function onResizeMouseMove(event: MouseEvent): void {
  if (!resizing.value) return;

  const dimensions = resizedDimensions(event, resizing.value);
  if (!dimensions) return;

  const field = props.fields.find(entry => entry.uid === resizing.value?.uid);
  if (field) {
    field.width = dimensions.width;
    field.height = dimensions.height;
  }
}

function onResizeMouseUp(event: MouseEvent): void {
  const state = resizing.value;
  stopResizeInteraction();

  if (!state) return;

  const dimensions = resizedDimensions(event, state);
  if (!dimensions) return;

  emit('field-resized', { uid: state.uid, ...dimensions });
}

onMounted(() => {
  updateOverlaySize();

  if (overlayRef.value) {
    overlayResizeObserver = new ResizeObserver(updateOverlaySize);
    overlayResizeObserver.observe(overlayRef.value);
  }
});

onBeforeUnmount(() => {
  overlayResizeObserver?.disconnect();
  overlayResizeObserver = null;
  stopDrawInteraction();
  stopMoveInteraction();
  stopResizeInteraction();
});

const drawPreviewColor = gaiaSignerPalette[0];
</script>

<template>
  <div
    ref="overlayRef"
    class="gaia-signature-field-canvas"
    data-tour="signing.field-overlay"
    :data-signature-page="props.currentPage"
    @dragover="onDragOver"
    @drop="onDrop"
    @mousedown="onOverlayMouseDown"
  >
    <gaia-placed-field
      v-if="drawPreview"
      preview
      :color="drawPreviewColor"
      :left="drawPreview.left"
      :top="drawPreview.top"
      :width="drawPreview.width"
      :height="drawPreview.height"
    />

    <gaia-placed-field
      v-for="field in currentPageFields"
      :key="field.uid"
      data-tour="signing.field-box"
      :label="fieldLabel(field)"
      :who="initialsOf(partyName(field.recipient_id))"
      :color="partyColor(field.recipient_id)"
      :left="`${field.x}%`"
      :top="`${field.y}%`"
      :width="`${renderedDimensions(field).width}%`"
      :height="`${renderedDimensions(field).height}%`"
      :text="fieldPreview(field)?.text ?? null"
      :sample="fieldPreview(field)?.fixed === false"
      :overflow="fieldPreview(field)?.fits === false"
      :lines="fieldPreview(field)?.lines ?? null"
      :font-size="fieldFontSize(field)"
      :selected="field.uid === props.selectedFieldUid"
      @mousedown="(event: MouseEvent) => onFieldMouseDown(event, field)"
      @select="emit('field-clicked', field.uid)"
      @remove="emit('field-removed', field.uid)"
      @resize-start="event => onResizeStart(event, field)"
    />
  </div>
</template>

<style scoped>
/* Fills the rendered page and takes the pointer. The crosshair is what tells
   the user a field can be drawn here; naive has no equivalent surface. */
.gaia-signature-field-canvas {
  position: absolute;
  inset: 0;
  pointer-events: all;
  cursor: crosshair;
}
</style>
